import React, { useEffect, useState } from 'react';
import { ExportCoach, exportSessions, getExportCoaches } from '../services/service';

const SessionsExport: React.FC = () => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [coaches, setCoaches] = useState<ExportCoach[]>([]);
  const [coachId, setCoachId] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    getExportCoaches()
      .then((items) => { if (active) setCoaches(items); })
      .catch(() => { if (active) setLoadError(true); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  const filteredCoaches = coaches.filter((coach) =>
    coach.name.toLocaleLowerCase('fr').includes(search.trim().toLocaleLowerCase('fr')));
  // Preserve the chosen coach even while searching for a different one.
  const selectedCoach = coaches.find((coach) => String(coach.id) === coachId);
  const invalidDates = !!startDate && !!endDate && startDate > endDate;

  const download = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!startDate || !endDate || invalidDates || loading || loadError || exporting) return;
    setExporting(true);
    setError('');
    try {
      const blob = await exportSessions(startDate, endDate, coachId ? Number(coachId) : undefined);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `cours_${startDate}_${endDate}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      setError("L'export a échoué. Réessayez.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <section className="panel">
      <div className="panel-head">
        <h3>Exporter les cours</h3>
        <p>Dates des séances incluses, avec les séances annulées / refusées.</p>
      </div>
      <form className="page-stack" onSubmit={download}>
        <div className="form-grid">
          <label>
            Date de début
            <input type="date" required min="1753-01-01" max="9999-12-30" value={startDate}
              onChange={(e) => setStartDate(e.target.value)} disabled={exporting} />
          </label>
          <label>
            Date de fin
            <input type="date" required min={startDate || '1753-01-01'} max="9999-12-30" value={endDate}
              onChange={(e) => setEndDate(e.target.value)} disabled={exporting} />
          </label>
          <label>
            Rechercher un coach
            <input type="search" placeholder="Nom ou prénom" value={search}
              onChange={(e) => setSearch(e.target.value)} disabled={loading || loadError || exporting} />
          </label>
          <label>
            Coach
            <select value={coachId} onChange={(e) => setCoachId(e.target.value)} disabled={loading || loadError || exporting}>
              <option value="">Tous les coachs</option>
              {selectedCoach && !filteredCoaches.includes(selectedCoach) && (
                <option value={selectedCoach.id}>{selectedCoach.name} (#{selectedCoach.id}) — sélectionné</option>
              )}
              {filteredCoaches.map((coach) => (
                <option key={coach.id} value={coach.id}>{coach.name} (#{coach.id})</option>
              ))}
            </select>
          </label>
        </div>
        {loading && <p role="status">Chargement des coachs…</p>}
        {loadError && <p className="error-line" role="alert">Impossible de charger les coachs. Rechargez la page.</p>}
        {!loading && !loadError && filteredCoaches.length === 0 && <p>Aucun coach trouvé.</p>}
        {invalidDates && <p className="error-line" role="alert">La date de fin doit être égale ou postérieure à la date de début.</p>}
        {error && <p className="error-line" role="alert">{error}</p>}
        <div>
          <button className="primary-btn" type="submit" disabled={!startDate || !endDate || invalidDates || loading || loadError || exporting}>
            {exporting ? 'Export en cours…' : 'Exporter en CSV'}
          </button>
        </div>
      </form>
    </section>
  );
};

export default SessionsExport;
